import {createSelector} from 'reselect';

import firmwareStatusTypes from '../../consts/device/firmwareStatusTypes';
import deviceTypes from '../../enums/device/deviceTypes';
import deviceTypesService from '../../services/device/deviceTypesService';
import fwuTargetService from '../../services/device/fwuTargetService';
import fwuPackageService from '../../services/fwu/fwuPackageService';
import iotDeviceConnectionStatusService from '../../services/iotDevice/iotDeviceConnectionStatusService';
import iotDeviceSettingsService from '../../services/iotDevice/iotDeviceSettingsService';
import productHelpers from '../../services/product/productHelpers';
import objUtils from '../../utils/objUtils';

const selectState = (state) => state.iotDevice;

const selectIotDevice = createSelector(selectState, (state) => state);
const selectIsIotDeviceDataExists = createSelector(selectIotDevice, (iotDevice) => !!iotDevice.data);

const selectDeviceConnectionStatus = createSelector(selectIotDevice, (iotDevice) => iotDevice.deviceConnectionStatus);

const selectDeviceActivationStatus = createSelector(selectIotDevice, (iotDevice) => iotDevice.isDeviceActivated);

const selectIsUpdateSettingsNeeded = createSelector(selectIotDevice, (iotDevice) => iotDevice.isUpdateSettingsNeeded);

const selectIsDeviceDisconnected = createSelector(selectDeviceConnectionStatus, (status) =>
    iotDeviceConnectionStatusService.isDeviceDisconnected(status)
);

const selectIsDevicePaired = createSelector(selectDeviceConnectionStatus, (status) =>
    iotDeviceConnectionStatusService.isDevicePaired(status)
);

const selectIsDeviceConnected = createSelector(selectDeviceConnectionStatus, (status) =>
    iotDeviceConnectionStatusService.isDeviceConnected(status)
);

const selectIsPairingInProgress = createSelector(selectIotDevice, (iotDevice) => iotDevice.isPairingInProgress);

const selectIsDeviceReady = createSelector(selectDeviceConnectionStatus, (status) =>
    iotDeviceConnectionStatusService.isDeviceReady(status)
);

const selectIsWrongDevice = createSelector(selectDeviceConnectionStatus, (status) =>
    iotDeviceConnectionStatusService.isWrongDevice(status)
);

const selectIsDeviceNotSupported = createSelector(selectDeviceConnectionStatus, (status) =>
    iotDeviceConnectionStatusService.isDeviceNotSupported(status)
);

const selectIotDeviceData = createSelector(selectIotDevice, (iotDevice) => {
    const iotData = iotDevice?.data;

    if (!iotData) return objUtils.returnEmptyObjectIfUndefined(iotData);

    const {device, holder} = iotData;
    const {type, codentify, isHolderConnected, deviceSerialNumber} = device || {};
    const isDeviceLocked = iotDeviceSettingsService.isDeviceLocked(iotData);
    const {codentify: holderCodentify} = holder || {};
    const isP4 = deviceTypesService.isP4(type);
    const isMono = deviceTypesService.isMono(type);
    const isP1V4 = deviceTypesService.isP1V4(type);
    const parentProtectionMode = isP1V4 && !isHolderConnected && !isMono ? false : isDeviceLocked;
    const isDeviceCodentifyValid = productHelpers.isOriginalCodentifyValid(codentify);
    const isHolderCodentifyValid = productHelpers.isOriginalCodentifyValid(holderCodentify);
    const preparedDevice = {
        ...device,
        codentify,
        codentifyOriginal: isDeviceCodentifyValid ? codentify : undefined,
        parentProtectionMode,
        isP4,
        isP1V4,
    };

    if (holder) {
        holder.codentifyOriginal = isHolderCodentifyValid ? holderCodentify : undefined;
        holder.typeOriginal = holder.type;
        holder.type = deviceTypesService.mapHolderTypeToChargerType(holder.type, type);
    }

    if (iotData && !iotData.deviceSerialNumber) {
        iotData.deviceSerialNumber = deviceSerialNumber;
    }

    const iotDeviceData = {...iotData, device: preparedDevice, holder};

    return iotDeviceData;
});

const selectIsHolderConnected = createSelector(
    selectIotDeviceData,
    (iotDevice) => iotDevice?.device?.isHolderConnected
);

const selectIsHolderConnectedOnInit = createSelector(
    selectIotDeviceData,
    (iotDevice) => iotDevice?.isHolderConnectedOnInit
);

const selectFirmwareState = createSelector(selectIotDevice, (iotDevice) =>
    objUtils.returnEmptyObjectIfUndefined(iotDevice?.firmware)
);

const selectFirmwareProgress = createSelector(selectFirmwareState, (state) => state.progress);

const makeSelectFirmwareProgressByFwuTarget = (fwuTarget) =>
    createSelector(selectFirmwareState, (state) => (state.fwuTarget === fwuTarget ? state.progress : 0));

const selectIsFirmwareError = createSelector(selectFirmwareState, (state) =>
    firmwareStatusTypes.isError(state.firmwareStatus)
);

const selectFirmwareError = createSelector(selectFirmwareState, (state) => state.error);

const selectIsFirmwareStarted = createSelector(selectFirmwareState, (state) =>
    firmwareStatusTypes.isStarted(state.firmwareStatus)
);

const selectIsFirmwareFinished = createSelector(selectFirmwareState, (state) =>
    firmwareStatusTypes.isFinished(state.firmwareStatus)
);

const selectIsFirmwareSuccess = createSelector(selectFirmwareState, (state) =>
    firmwareStatusTypes.isSuccess(state.firmwareStatus)
);

const selectIsFirmwareChecking = createSelector(selectFirmwareState, (state) =>
    firmwareStatusTypes.isChecking(state.firmwareStatus)
);

const makeSelectIsFirmwareProcessStarted = (fwuTarget) =>
    createSelector(makeSelectFirmwareProgressByFwuTarget(fwuTarget), (progress) => progress > 0);

const selectIsFirmwareInProgress = createSelector(
    selectIsFirmwareChecking,
    selectIsFirmwareStarted,
    selectIsFirmwareFinished,
    (isChecking, isStarted, isFinished) => isChecking || isStarted || isFinished
);

const FWU_COMPLETED_PROGRESS = 100;

const selectIsFirmwareCompleted = createSelector(
    selectIsFirmwareInProgress,
    selectFirmwareProgress,
    (isFirmwareInProgress, progress) => isFirmwareInProgress && progress === FWU_COMPLETED_PROGRESS
);

const selectFirmwareFwuTarget = createSelector(selectFirmwareState, (state) => state.fwuTarget);

const selectIsFirmwareTargetDevice = createSelector(selectFirmwareFwuTarget, (fwuTarget) =>
    fwuTargetService.isFwuTargetDevice(fwuTarget)
);

const selectIsFirmwareTargetBleStack = createSelector(selectFirmwareFwuTarget, (fwuTarget) =>
    fwuTargetService.isFwuTargetBleStack(fwuTarget)
);

const selectIotDeviceType = createSelector(selectIotDevice, (iotDevice) => iotDevice?.data?.device?.type);

const selectIotHolderType = createSelector(selectIotDevice, (iotDevice) => iotDevice?.data?.holder?.type);

const selectIotDeviceCodentify = createSelector(selectIotDevice, (iotDevice) => iotDevice?.data?.device?.codentify);

const selectIotHolderCodentify = createSelector(selectIotDevice, (iotDevice) => iotDevice?.data?.holder?.codentify);

const selectIsIotDeviceP4 = createSelector(selectIotDeviceType, (type) => deviceTypesService.isP4(type));

const selectIsIotDeviceDelta = createSelector(selectIotDeviceType, (type) => deviceTypesService.isDelta(type));

const selectIotDeviceMaterialColor = createSelector(
    selectIotDevice,
    (iotDevice) => iotDevice?.data?.device?.materialColor
);

const selectGetFeatureTypesList = createSelector(selectState, (state) => state.getFeatureKeysList);

const makeSelectGetFeature = (type) =>
    createSelector(selectGetFeatureTypesList, (list) => (list && type ? list[type] : null));

const selectIsGetFeatureInitialized = createSelector(selectGetFeatureTypesList, (list) => list !== undefined);

const selectIsAnyGetFeature = createSelector(selectGetFeatureTypesList, (list) => objUtils.isAnyKeys(list));

const selectIsDeviceInSblMode = createSelector(
    selectIotDevice,
    (iotDevice) => iotDevice?.data?.device?.is_device_in_sbl_mode
);

const selectDeviceBattery = createSelector(selectIotDevice, (iotDevice) => iotDevice?.data?.device?.battery_charge);

const selectIsMonoDevice = createSelector(selectIotDeviceType, (type) => {
    return deviceTypesService.isMono(type);
});

const selectProductName = createSelector(selectIotDeviceType, (type) => deviceTypesService.getProductNameByType(type));

const makeSelectAmplitudeModelName = (deviceType) =>
    createSelector(selectIotDeviceType, (type) => {
        if (deviceType === deviceTypes.DEV_TYPE_P1V41A_CONTROL || type === deviceTypes.DEV_TYPE_P1V41A_CONTROL) {
            return 'IQOS ILUMA i ONE A';
        }

        return deviceTypesService.getDeviceModelNameByType(deviceType || type);
    });

const selectDeviceSerialNumber = createSelector(selectIotDeviceData, (iotDevice) => iotDevice?.deviceSerialNumber);

const selectHolderSerialNumber = createSelector(
    selectIotDeviceData,

    (iotDevice) => iotDevice?.holder?.deviceSerialNumber
);

const makeSelectSerialNumber = (isHolder) =>
    createSelector(selectDeviceSerialNumber, selectHolderSerialNumber, (deviceSerialNumber, holderSerialNumber) =>
        isHolder ? holderSerialNumber : deviceSerialNumber
    );

const selectLeviaRelatedTarget = createSelector(selectIsMonoDevice, selectIotDevice, (isMonoDevice, iotDevice) => {
    const target = isMonoDevice ? iotDevice?.data?.device : iotDevice?.data?.holder;

    return target;
});

const selectLeviaRelatedTargetSerialNumber = createSelector(selectLeviaRelatedTarget, (target) => {
    return target?.deviceSerialNumber;
});

const selectFirmware_124_4_4_RelatedTargetSerialNumber = createSelector(selectIotDeviceData, (iotDevice) => {
    return iotDevice?.holder?.deviceSerialNumber;
});

const selectDeviceLeviaState = createSelector(selectLeviaRelatedTarget, (target) => {
    const featureState = target?.engineKodak?.featureState;

    return featureState;
});

const makeSelectIsLeviaDevice = (isNeedHolderCheck = true) =>
    createSelector(
        selectIotDeviceType,
        selectIsHolderConnected,
        selectIsMonoDevice,
        (type, isHolderConnected, isMonoDevice) => {
            const isP1V4 = deviceTypesService.isP1V4(type);

            if (isP1V4 && isMonoDevice) return true;

            if (isNeedHolderCheck && !isHolderConnected) return false;

            return isP1V4;
        }
    );

const selectIsLeviaFwuAvailable = createSelector(selectIsIotDeviceDelta, selectIotDevice, (isDelta, iotDevice) => {
    const result = fwuPackageService.checkIfLeviaFwuAvailable({
        iotDevice: iotDevice?.data,
        isDelta,
    });

    return result;
});

const selectFeaturesConfigUpdateDate = createSelector(
    selectIotDevice,
    (iotDevice) => iotDevice.featuresConfigUpdateDate
);

export {
    makeSelectAmplitudeModelName,
    makeSelectFirmwareProgressByFwuTarget,
    makeSelectGetFeature,
    makeSelectIsFirmwareProcessStarted,
    makeSelectIsLeviaDevice,
    makeSelectSerialNumber,
    selectDeviceActivationStatus,
    selectDeviceBattery,
    selectDeviceConnectionStatus,
    selectDeviceLeviaState,
    selectDeviceSerialNumber,
    selectFeaturesConfigUpdateDate,
    selectFirmware_124_4_4_RelatedTargetSerialNumber,
    selectFirmwareError,
    selectFirmwareFwuTarget,
    selectFirmwareProgress,
    selectFirmwareState,
    selectGetFeatureTypesList,
    selectHolderSerialNumber,
    selectIotDevice,
    selectIotDeviceCodentify,
    selectIotDeviceData,
    selectIotDeviceMaterialColor,
    selectIotDeviceType,
    selectIotHolderCodentify,
    selectIotHolderType,
    selectIsAnyGetFeature,
    selectIsDeviceConnected,
    selectIsDeviceDisconnected,
    selectIsDeviceInSblMode,
    selectIsDeviceNotSupported,
    selectIsDevicePaired,
    selectIsDeviceReady,
    selectIsFirmwareChecking,
    selectIsFirmwareCompleted,
    selectIsFirmwareError,
    selectIsFirmwareFinished,
    selectIsFirmwareInProgress,
    selectIsFirmwareStarted,
    selectIsFirmwareSuccess,
    selectIsFirmwareTargetBleStack,
    selectIsFirmwareTargetDevice,
    selectIsGetFeatureInitialized,
    selectIsHolderConnected,
    selectIsHolderConnectedOnInit,
    selectIsIotDeviceDataExists,
    selectIsIotDeviceDelta,
    selectIsIotDeviceP4,
    selectIsLeviaFwuAvailable,
    selectIsMonoDevice,
    selectIsPairingInProgress,
    selectIsUpdateSettingsNeeded,
    selectIsWrongDevice,
    selectLeviaRelatedTargetSerialNumber,
    selectProductName,
};
